import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import { AUTH_TOKEN_KEY, FALLBACK_API_ERROR } from '@/utils/consts'
import { AuthV2Service } from '@/services/auth-v2-service'
import type { CheckResponse } from '@/services/auth-v2-service/types'
import axios from 'axios'
import type { ApiError, accessInfo } from '@/utils/types'
import type { UserAuthRequest } from '@/services/auth-v2-service/types'

type ActionFlags = 'check' | 'auth' | 'setTz'
type AccessTypes =
  | 'spot_spot'
  | 'spot_futures'
  | 'futures_futures'
  | 'futures_advanced'
  | 'hunter'
  | 'dex'
  | 'dex_cex_free'
  | 'dex_cex'
  | 'dex_cex_advanced'
  | 'dex_lite'
  | 'dex_pro'
  | 'listings'
  | 'trade_on_click'
  | 'autotrade'
  | 'futures'
  | 'futures_wo_auto'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem(AUTH_TOKEN_KEY),
    authorized: false,
    prevResponse: {} as CheckResponse,
    lastCheck: null as number | null,
    tz: null as string | null,
    loading: {} as Record<ActionFlags, boolean>,
    currentAccessList: {} as Record<AccessTypes, accessInfo>,
    settings: {} as Record<string, string>,
    error: {} as Record<ActionFlags, ApiError | null>,
    profiles: { cex_cex: [] as number[], dex_cex: [] as number[] },
    refCode: null as string | null,
    refBalance: null as number | null,
    refRatio: null as number | null,
    router: useRouter(),
  }),
  actions: {
    logout() {
      localStorage.removeItem(AUTH_TOKEN_KEY)
    },
    getToken() {
      return localStorage.getItem(AUTH_TOKEN_KEY)
    },
    async check(ignoreState = false): Promise<CheckResponse | undefined> {
      if (!this.getToken()) {
        this.authorized = false
        return
      }
      if (this.lastCheck) {
        const delta = +new Date() - this.lastCheck
        if (delta < 5000) {
          return this.prevResponse
        }
      }
      if (!ignoreState) {
        this.loading['check'] = true
      }
      try {
        const req = await AuthV2Service.checkToken()
        this.prevResponse = req.data
        this.currentAccessList = req?.data.current_access_list as Record<AccessTypes, accessInfo>
        this.settings = req?.data.settings
        const rawProfiles = req?.data.profiles || { cex_cex: [], dex_cex: [] }
        this.profiles = {
          cex_cex: [...(rawProfiles.cex_cex || [])].sort((a, b) => a - b),
          dex_cex: [...(rawProfiles.dex_cex || [])].sort((a, b) => a - b),
        }
        this.authorized = true
        this.tz = req?.data.tz
        this.refCode = req?.data.ref_code ?? null
        this.refBalance = req?.data.ref_balance ?? null
        this.refRatio = req?.data.ref_ratio ?? null
        this.lastCheck = +new Date()
        return req?.data
      } catch (thrown) {
        if (axios.isAxiosError(thrown) && thrown.response) {
          this.error.check = {
            message: thrown.response?.data,
            code: thrown.response?.status,
          }
        } else {
          this.error.check = FALLBACK_API_ERROR
          console.error(thrown)
        }
        this.authorized = false
      } finally {
        this.loading['check'] = false
      }
    },
    async authTg(data: UserAuthRequest, refCode?: string | null) {
      this.loading['auth'] = true
      try {
        const req = await AuthV2Service.tgAuth(data, refCode)
        this.authorized = true
        this.token = req.data.access_token
        localStorage.setItem(AUTH_TOKEN_KEY, this.token)
        this.error.auth = null
        return req.data
      } catch (thrown) {
        if (axios.isAxiosError(thrown) && thrown.response) {
          this.error.auth = {
            message: thrown.response?.data,
            code: thrown.response?.status,
          }
        } else {
          this.error.auth = FALLBACK_API_ERROR
        }
        this.authorized = false
      } finally {
        this.loading['auth'] = false
      }
    },
    async setTimezone(timezone: string) {
      this.loading.setTz = true
      try {
        await AuthV2Service.setTimezone(timezone)
      } catch (thrown) {
        if (axios.isAxiosError(thrown) && thrown.response) {
          this.error.setTz = {
            message: thrown.response?.data,
            code: thrown.response?.status,
          }
        } else {
          this.error.setTz = FALLBACK_API_ERROR
        }
      } finally {
        this.loading.setTz = false
      }
    },
  },
})
